import React from 'react';
import { Link } from 'react-router-dom';
import { Book, Code, Settings, Globe, BarChart3, TrendingUp, Map, Shield, Database } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';

const Documentation = () => {
  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center py-8">
        <h1 className="text-4xl font-bold mb-4">Trendy Documentation</h1>
        <p className="text-lg text-reddit-600 dark:text-dark-textMuted max-w-3xl mx-auto">
          Complete guide to using and understanding the Trendy trending topics analyzer
        </p>
      </div>

      {/* Table of Contents */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Book className="w-6 h-6 text-reddit-orange" />
            Table of Contents
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <button onClick={() => document.getElementById('getting-started')?.scrollIntoView({ behavior: 'smooth' })} className="block p-3 rounded-lg hover:bg-reddit-50 dark:hover:bg-dark-border transition-colors text-left">
              <span className="font-medium">Getting Started</span>
            </button>
            <button onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })} className="block p-3 rounded-lg hover:bg-reddit-50 dark:hover:bg-dark-border transition-colors text-left">
              <span className="font-medium">Features Overview</span>
            </button>
            <button onClick={() => document.getElementById('usage')?.scrollIntoView({ behavior: 'smooth' })} className="block p-3 rounded-lg hover:bg-reddit-50 dark:hover:bg-dark-border transition-colors text-left">
              <span className="font-medium">How to Use</span>
            </button>
            <button onClick={() => document.getElementById('api')?.scrollIntoView({ behavior: 'smooth' })} className="block p-3 rounded-lg hover:bg-reddit-50 dark:hover:bg-dark-border transition-colors text-left">
              <span className="font-medium">API Integration</span>
            </button>
            <button onClick={() => document.getElementById('customization')?.scrollIntoView({ behavior: 'smooth' })} className="block p-3 rounded-lg hover:bg-reddit-50 dark:hover:bg-dark-border transition-colors text-left">
              <span className="font-medium">Customization</span>
            </button>
            <button onClick={() => document.getElementById('troubleshooting')?.scrollIntoView({ behavior: 'smooth' })} className="block p-3 rounded-lg hover:bg-reddit-50 dark:hover:bg-dark-border transition-colors text-left">
              <span className="font-medium">Troubleshooting</span>
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Getting Started */}
      <section id="getting-started" className="space-y-4">
        <h2 className="text-3xl font-bold">Getting Started</h2>
        <p className="text-reddit-600 dark:text-dark-textMuted">
          Learn how to set up and start using Trendy
        </p>

        <Card>
          <CardHeader>
            <CardTitle>Installation</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p>Trendy is a web application that requires no installation. Simply visit the website and start exploring trending topics immediately.</p>

            <div className="bg-reddit-50 dark:bg-dark-border p-4 rounded-lg">
              <h4 className="font-semibold mb-2">System Requirements</h4>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>Modern web browser (Chrome, Firefox, Safari, Edge)</li>
                <li>Internet connection for real-time data</li>
                <li>JavaScript enabled</li>
                <li>Minimum screen resolution: 1024x768</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>First Time Setup</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <ol className="list-decimal list-inside space-y-2">
              <li>Visit the Trendy website</li>
              <li>Choose your preferred theme (Light/Dark mode)</li>
              <li>Explore the different sections using the navigation menu</li>
              <li>Customize your preferences in the Settings page</li>
              <li>Start discovering trending topics!</li>
            </ol>

            <div className="bg-reddit-50 dark:bg-dark-border p-4 rounded-lg">
              <h4 className="font-semibold mb-2">Quick Tips</h4>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>Use the search bar to find specific topics</li>
                <li>Click on trends to see detailed information</li>
                <li>Add trends to favorites for quick access</li>
                <li>Use filters to narrow down results by category, region, or source</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Features Overview */}
      <section id="features" className="space-y-4">
        <h2 className="text-3xl font-bold">Features Overview</h2>
        <p className="text-reddit-600 dark:text-dark-textMuted">
          Discover all the powerful features Trendy offers
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardContent className="pt-6 text-center">
              <div className="w-16 h-16 bg-reddit-orange rounded-full flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Real-Time Trends</h3>
              <p className="text-reddit-600 dark:text-dark-textMuted">
                Get instant updates on trending topics from multiple sources including Twitter, Reddit, and Google Trends
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6 text-center">
              <div className="w-16 h-16 bg-reddit-blue rounded-full flex items-center justify-center mx-auto mb-4">
                <Map className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Global Heatmap</h3>
              <p className="text-reddit-600 dark:text-dark-textMuted">
                Visualize trending topics by region with interactive heatmap visualization
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6 text-center">
              <div className="w-16 h-16 bg-reddit-green rounded-full flex items-center justify-center mx-auto mb-4">
                <BarChart3 className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Analytics Dashboard</h3>
              <p className="text-reddit-600 dark:text-dark-textMuted">
                Comprehensive insights with multiple chart types and statistical analysis
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6 text-center">
              <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Globe className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Multi-Region Support</h3>
              <p className="text-reddit-600 dark:text-dark-textMuted">
                Track trends across different regions: Global, North America, Europe, Asia, and more
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* How to Use */}
      <section id="usage" className="space-y-4">
        <h2 className="text-3xl font-bold">How to Use Trendy</h2>
        <p className="text-reddit-600 dark:text-dark-textMuted">
          Step-by-step guides for using Trendy's features
        </p>

        <Card>
          <CardHeader>
            <CardTitle>Browsing Trends</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <ol className="list-decimal list-inside space-y-2">
              <li>Navigate to the <Link to="/trends" className="text-reddit-orange hover:underline">Trends</Link> page</li>
              <li>Use the filter options to select category, region, and source</li>
              <li>Browse through the list of trending topics</li>
              <li>Click on any trend card to see detailed information</li>
              <li>Use the search bar to find specific topics</li>
            </ol>

            <div className="bg-reddit-50 dark:bg-dark-border p-4 rounded-lg">
              <h4 className="font-semibold mb-2">Filter Options</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="font-medium">Category:</span> Technology, Politics, Sports, Entertainment, Business
                </div>
                <div>
                  <span className="font-medium">Region:</span> Global, North America, Europe, Asia, etc.
                </div>
                <div>
                  <span className="font-medium">Source:</span> Twitter, Reddit, Google Trends
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Using Favorites</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <ol className="list-decimal list-inside space-y-2">
              <li>Browse trends on the Trends page</li>
              <li>Click "Add to Favorites" on any trend you want to save</li>
              <li>Switch to "Favorites" view to see your saved trends</li>
              <li>Click "Remove from Favorites" to remove a trend</li>
            </ol>

            <div className="bg-reddit-50 dark:bg-dark-border p-4 rounded-lg">
              <h4 className="font-semibold mb-2">Favorites Features</h4>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>Save trends for quick access</li>
                <li>Favorites are stored locally in your browser</li>
                <li>Easily manage your favorite trends</li>
                <li>Favorites persist between sessions</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Using the Heatmap</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <ol className="list-decimal list-inside space-y-2">
              <li>Navigate to the <Link to="/heatmap" className="text-reddit-orange hover:underline">Heatmap</Link> page</li>
              <li>View the global trends heatmap visualization</li>
              <li>Click on any region to see detailed trends for that area</li>
              <li>Use the legend to understand activity levels</li>
              <li>Explore top trends in each region</li>
            </ol>

            <div className="bg-reddit-50 dark:bg-dark-border p-4 rounded-lg">
              <h4 className="font-semibold mb-2">Heatmap Legend</h4>
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-1">
                  <div className="w-4 h-4 bg-orange-200 rounded"></div>
                  <span>Low activity</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-4 h-4 bg-orange-300 rounded"></div>
                  <span>Medium activity</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-4 h-4 bg-orange-400 rounded"></div>
                  <span>High activity</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-4 h-4 bg-reddit-orange rounded"></div>
                  <span>Very high activity</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* API Integration */}
      <section id="api" className="space-y-4">
        <h2 className="text-3xl font-bold">API Integration</h2>
        <p className="text-reddit-600 dark:text-dark-textMuted">
          Information about data sources and API integration
        </p>

        <Card>
          <CardHeader>
            <CardTitle>Data Sources</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p>Trendy currently uses mock data for demonstration purposes. The application is designed to integrate with real APIs when credentials are provided.</p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-reddit-50 dark:bg-dark-border p-4 rounded-lg">
                <h4 className="font-semibold mb-2 flex items-center gap-2">
                  <Database className="w-5 h-5 text-reddit-blue" />
                  Twitter API
                </h4>
                <p className="text-sm">Real-time trending topics from Twitter</p>
                <Badge variant="green" className="mt-2">Available</Badge>
              </div>

              <div className="bg-reddit-50 dark:bg-dark-border p-4 rounded-lg">
                <h4 className="font-semibold mb-2 flex items-center gap-2">
                  <Database className="w-5 h-5 text-reddit-orange" />
                  Reddit API
                </h4>
                <p className="text-sm">Popular topics from Reddit communities</p>
                <Badge variant="green" className="mt-2">Available</Badge>
              </div>

              <div className="bg-reddit-50 dark:bg-dark-border p-4 rounded-lg">
                <h4 className="font-semibold mb-2 flex items-center gap-2">
                  <Database className="w-5 h-5 text-reddit-green" />
                  Google Trends
                </h4>
                <p className="text-sm">Search trends from Google</p>
                <Badge variant="green" className="mt-2">Available</Badge>
              </div>
            </div>

            <div className="bg-reddit-50 dark:bg-dark-border p-4 rounded-lg">
              <h4 className="font-semibold mb-2">API Configuration</h4>
              <p className="text-sm mb-2">To integrate real APIs:</p>
              <ol className="list-decimal list-inside space-y-1 text-sm">
                <li>Copy <code>.env.example</code> to <code>.env.local</code></li>
                <li>Add your API credentials to the environment file</li>
                <li>Update the service layer in <code>src/services/trendingService.js</code></li>
                <li>Restart the application</li>
              </ol>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>API Endpoints</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-reddit-200 dark:border-dark-border">
                    <th className="text-left p-2 font-medium">Endpoint</th>
                    <th className="text-left p-2 font-medium">Description</th>
                    <th className="text-left p-2 font-medium">Parameters</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-reddit-100 dark:border-dark-border">
                    <td className="p-2"><code>/api/trends</code></td>
                    <td className="p-2">Get trending topics</td>
                    <td className="p-2">category, region, source, limit</td>
                  </tr>
                  <tr className="border-b border-reddit-100 dark:border-dark-border">
                    <td className="p-2"><code>/api/trends/{id}</code></td>
                    <td className="p-2">Get trend details</td>
                    <td className="p-2">id</td>
                  </tr>
                  <tr className="border-b border-reddit-100 dark:border-dark-border">
                    <td className="p-2"><code>/api/heatmap</code></td>
                    <td className="p-2">Get regional trends data</td>
                    <td className="p-2">-</td>
                  </tr>
                  <tr className="border-b border-reddit-100 dark:border-dark-border">
                    <td className="p-2"><code>/api/analytics</code></td>
                    <td className="p-2">Get analytics summary</td>
                    <td className="p-2">period, region</td>
                  </tr>
                  <tr>
                    <td className="p-2"><code>/api/search</code></td>
                    <td className="p-2">Search trends</td>
                    <td className="p-2">query, limit</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Customization */}
      <section id="customization" className="space-y-4">
        <h2 className="text-3xl font-bold">Customization</h2>
        <p className="text-reddit-600 dark:text-dark-textMuted">
          Personalize your Trendy experience
        </p>

        <Card>
          <CardHeader>
            <CardTitle>Theme Customization</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p>Trendy supports both light and dark themes with a Reddit-inspired color scheme.</p>

            <div className="flex gap-4">
              <div className="flex-1 text-center">
                <div className="w-full h-32 bg-white border border-reddit-200 rounded-lg mb-2 flex items-center justify-center">
                  <span className="text-reddit-600">Light Theme</span>
                </div>
                <p className="font-medium">Light Mode</p>
              </div>
              <div className="flex-1 text-center">
                <div className="w-full h-32 bg-dark-bg border border-dark-border rounded-lg mb-2 flex items-center justify-center">
                  <span className="text-dark-text">Dark Theme</span>
                </div>
                <p className="font-medium">Dark Mode</p>
              </div>
            </div>

            <div className="bg-reddit-50 dark:bg-dark-border p-4 rounded-lg">
              <h4 className="font-semibold mb-2">How to Change Theme</h4>
              <ol className="list-decimal list-inside space-y-1 text-sm">
                <li>Click the moon/sun icon in the top navigation bar</li>
                <li>Choose between Light or Dark mode</li>
                <li>Your preference is saved automatically</li>
              </ol>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Settings Options</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p>Customize your Trendy experience with various settings options.</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-reddit-50 dark:bg-dark-border p-4 rounded-lg">
                <h4 className="font-semibold mb-2 flex items-center gap-2">
                  <Settings className="w-5 h-5 text-reddit-orange" />
                  General Settings
                </h4>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>Default region and category</li>
                  <li>Auto-refresh preferences</li>
                  <li>Notification settings</li>
                </ul>
              </div>

              <div className="bg-reddit-50 dark:bg-dark-border p-4 rounded-lg">
                <h4 className="font-semibold mb-2 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-reddit-blue" />
                  Privacy Settings
                </h4>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>Data retention controls</li>
                  <li>Clear cache and history</li>
                  <li>Export your data</li>
                </ul>
              </div>
            </div>

            <Link to="/settings">
              <Button variant="primary" className="w-full">
                <Settings className="w-4 h-4 mr-2" />
                Go to Settings
              </Button>
            </Link>
          </CardContent>
        </Card>
      </section>

      {/* Troubleshooting */}
      <section id="troubleshooting" className="space-y-4">
        <h2 className="text-3xl font-bold">Troubleshooting</h2>
        <p className="text-reddit-600 dark:text-dark-textMuted">
          Common issues and solutions
        </p>

        <Card>
          <CardHeader>
            <CardTitle>Common Issues</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <h4 className="font-semibold mb-2">Trends not loading</h4>
              <ul className="list-disc list-inside space-y-1 text-sm pl-4">
                <li>Check your internet connection</li>
                <li>Refresh the page</li>
                <li>Clear browser cache</li>
                <li>Try a different browser</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-2">Search not working</h4>
              <ul className="list-disc list-inside space-y-1 text-sm pl-4">
                <li>Make sure you press Enter after typing</li>
                <li>Check for typos in your search query</li>
                <li>Try a broader search term</li>
                <li>Refresh the page and try again</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-2">Favorites not saving</h4>
              <ul className="list-disc list-inside space-y-1 text-sm pl-4">
                <li>Check if your browser supports localStorage</li>
                <li>Make sure you're not in private/incognito mode</li>
                <li>Check browser privacy settings</li>
                <li>Try clearing browser data and starting fresh</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-2">Theme not changing</h4>
              <ul className="list-disc list-inside space-y-1 text-sm pl-4">
                <li>Try refreshing the page</li>
                <li>Clear browser cache</li>
                <li>Check if localStorage is enabled</li>
                <li>Try in a different browser</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Browser Support</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="w-12 h-12 bg-reddit-100 dark:bg-dark-border rounded-full flex items-center justify-center mx-auto mb-2">
                  <span className="font-bold">C</span>
                </div>
                <p className="font-medium">Chrome</p>
                <Badge variant="green" className="text-xs">Supported</Badge>
              </div>

              <div className="text-center">
                <div className="w-12 h-12 bg-reddit-100 dark:bg-dark-border rounded-full flex items-center justify-center mx-auto mb-2">
                  <span className="font-bold">F</span>
                </div>
                <p className="font-medium">Firefox</p>
                <Badge variant="green" className="text-xs">Supported</Badge>
              </div>

              <div className="text-center">
                <div className="w-12 h-12 bg-reddit-100 dark:bg-dark-border rounded-full flex items-center justify-center mx-auto mb-2">
                  <span className="font-bold">S</span>
                </div>
                <p className="font-medium">Safari</p>
                <Badge variant="green" className="text-xs">Supported</Badge>
              </div>

              <div className="text-center">
                <div className="w-12 h-12 bg-reddit-100 dark:bg-dark-border rounded-full flex items-center justify-center mx-auto mb-2">
                  <span className="font-bold">E</span>
                </div>
                <p className="font-medium">Edge</p>
                <Badge variant="green" className="text-xs">Supported</Badge>
              </div>
            </div>

            <div className="mt-6 bg-reddit-50 dark:bg-dark-border p-4 rounded-lg">
              <h4 className="font-semibold mb-2">Mobile Support</h4>
              <p className="text-sm">Trendy is fully responsive and works on all modern mobile devices.</p>
              <ul className="list-disc list-inside space-y-1 text-sm mt-2">
                <li>iOS Safari</li>
                <li>Android Chrome</li>
                <li>Mobile Firefox</li>
                <li>Tablet browsers</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Back to Top */}
      <div className="text-center py-8">
        <Button
          variant="outline"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        >
          Back to Top
        </Button>
      </div>

      {/* Feedback Section */}
      <Card>
        <CardContent className="pt-6 text-center">
          <h3 className="text-xl font-semibold mb-2">Help Improve Trendy</h3>
          <p className="text-reddit-600 dark:text-dark-textMuted mb-4">
            Have questions or suggestions? We'd love to hear from you!
          </p>
          <div className="flex flex-wrap gap-2 justify-center">
            <Button variant="primary">
              <Code className="w-4 h-4 mr-2" />
              View Source Code
            </Button>
            <Button variant="outline">
              <Book className="w-4 h-4 mr-2" />
              Report Issue
            </Button>
            <Button variant="outline">
              <Book className="w-4 h-4 mr-2" />
              Suggest Feature
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Documentation;